import {
  CaseId,
  FolderId,
  TDOId,
  TreeObjectId,
} from '@cbsa/state/modules/universal';
import { Tag } from '@common/state/modules/mediaDetails/models';
import { FolderPathItem } from '@redact-modules/helper';

// Subset of the platform `veritoneFile` blob needed to resolve a file's type;
// shape matches `MediaFileLike` in `@helpers/mediaFile` so trash rows can use
// `resolveFileType`.
interface VeritoneFileMeta {
  mimetype?: string | null;
  mimeType?: string | null;
  fileType?: string | null;
}

export interface TrashTDO {
  id: TDOId;
  name: string;
  status: 'downloaded' | 'recording' | 'recorded';
  thumbnailUrl: string;
  createdDateTime: string;
  modifiedDateTime: string;
  startDateTime: string;
  stopDateTime: string;
  details: {
    readonly tags?: ReadonlyArray<
      | {
          redactionStatus: string;
          redactionStatusKey?: string;
          value: 'in redaction';
          oldFolderPath?: FolderPathItem[];
          trashRetentionDays?: string;
          /** Legacy stamp on items trashed before the trashRetentionDays rename. */
          numberOfDaysToKeep?: string;
          movedToTrashDateTime?: string;
          movedToTrashByName?: string;
          movedToTrashById?: string;
        }
      | Tag
      | null
    >;
    // Original upload metadata. Used as the file-type fallback for icons when a
    // file failed to ingest and so has no `primaryAsset`.
    readonly veritoneFile?: VeritoneFileMeta | null;
  };
  primaryAsset?: {
    contentType: string;
    signedUri?: string;
  };
  folders: {
    folderPath: FolderPathItem[];
  }[];
}

export interface TrashCaseFile {
  id: TDOId;
  name: string;
  createdDateTime: string;
  modifiedDateTime: string;
  startDateTime: string;
  stopDateTime: string;
  primaryAsset?: {
    contentType: string;
    signedUri?: string;
  };
  // See `TrashTDO.details.veritoneFile` — fallback for the file-type icon when
  // a case file failed to ingest and has no `primaryAsset`.
  details?: {
    readonly veritoneFile?: VeritoneFileMeta | null;
  };
}

export interface TrashSdoData {
  caseName: string;
  status: string;
  folderTreeObjectId: TreeObjectId;
  createdDateTime: string; // ISO
  modifiedDateTime: string; // ISO
  priority?: number;
  processingTime?: number;
  oldFolderPath?: FolderPathItem[];
  trashRetentionDays?: number;
  /** Legacy stamp on items trashed before the trashRetentionDays rename. */
  numberOfDaysToKeep?: number;
  movedToTrashDateTime?: string;
  movedToTrashByName?: string;
  movedToTrashById?: string;
  isCase?: boolean;
}

export interface TrashContentTemplate {
  id: string;
  schemaId: string;
  sdoId: string;
  // Null when the referenced SDO has been deleted out from under the link: the
  // API answers with a `not_found` error for this field and the folders fetch
  // keeps the rest of the record (returnPartialDataOnNotFound). A null `sdo`
  // therefore means "this SDO is already gone" — treat the link as an orphan to
  // unlink, never as an SDO still needing deletion (VE-25907).
  sdo?: {
    data: TrashSdoData;
  } | null;
}

export interface TrashFolder {
  id: FolderId | CaseId;
  name: string;
  treeObjectId: TreeObjectId;
  createdDateTime: string;
  modifiedDateTime: string;
  contentTemplates: ReadonlyArray<TrashContentTemplate>;
  // Resolved once at ingestion via pickTrashContentTemplate: the single content
  // template that carries the move-to-trash metadata. A trashed folder can hold
  // more than one SDO of the same schema (e.g. a leftover case SDO alongside the
  // trash SDO), so consumers MUST read trash metadata from this template rather
  // than from contentTemplates[0], whose order is server-dependent.
  trashContentTemplate?: TrashContentTemplate;
  folderPath: FolderPathItem[];
}

/**
 * Selects the content template that carries move-to-trash metadata.
 *
 * Only the trash SDO (created by createSoftDeletedFolderSdoQuery) has
 * `movedToTrashDateTime`, so we key off that. Falls back to the first template
 * for legacy / single-SDO folders, leaving their behaviour unchanged.
 *
 * Note: `isCase` is NOT a usable discriminator — it is `false` on the trash SDO
 * and absent on the leftover case SDO, so both read as falsy.
 */
export const pickTrashContentTemplate = (
  contentTemplates?: ReadonlyArray<TrashContentTemplate>
): TrashContentTemplate | undefined =>
  contentTemplates?.find((ct) => ct?.sdo?.data?.movedToTrashDateTime) ??
  contentTemplates?.[0];

export interface CaseFilesState {
  records: TrashCaseFile[];
  // N+1 lookahead flag: true when the fetched page had an extra row beyond the
  // page size, i.e. a next page exists. Replaces the (server-unavailable)
  // exact total count.
  hasNextPage: boolean;
  pageIndex: number;
  pageSize: number;
  isLoading: boolean;
  loadError: boolean;
  hasLoadedOnce: boolean;
}

export interface FolderStructuredDataObject {
  id: string; // SDO ID
  data: TrashSdoData;
}

export interface TrashState {
  files: {
    isFetchingData: boolean;
    selectedItems: TDOId[];
    // Snapshot of every selected TDO, keyed by id. `tdos` only holds the current
    // page, but selection survives paging, so restore needs this to resolve rows
    // ticked on other pages (their original folder path lives on the TDO).
    // Cleared when leaving Trash (CLEAR_TRASH_SELECTION): read it before any
    // await in a saga.
    selectedTdos: { [key: string]: TrashTDO };
    tdos: { [key: string]: TrashTDO };
    pageSize: number;
    pageIndex: number;
    hasNextPage: boolean;
  };
  folders: {
    isFetchingData: boolean;
    selectedItems: TreeObjectId[];
    // Same as `files.selectedTdos` (and the same lifetime rule), for folders
    // selected on other pages. Also drives the irreversible permanent delete.
    selectedFolders: { [key: string]: TrashFolder };
    folders: { [key: string]: TrashFolder };
    pageSize: number;
    pageIndex: number;
    hasNextPage: boolean;
    caseFiles: { [key: string]: CaseFilesState };
  };
}
